import { memo, useMemo, useState, useEffect } from 'react';
import { Calendar, AlertTriangle, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useSession } from "@/hooks/use-session";
import { TodayEvents } from "@/components/TodayEvents";
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useCalendarEvents } from '@/hooks/use-calendar-events';
import { Event, DayEvents } from '@/types/event';
import { DailyEventsList } from '@/components/DailyEventsList';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { EventDetails } from '@/components/EventDetails';

interface StrykrCalendarCardProps {
  className?: string;
}

// Helper to parse potentially string/null numeric values from API
const parseNumericEventValue = (value: string | number | null | undefined): number => {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    // Attempt to remove common non-numeric chars like '%' and parse
    const numericString = value.replace(/[^0-9.-]+/g, ""); 
    const parsed = parseFloat(numericString);
    return isNaN(parsed) ? 0 : parsed;
  }

  return 0; // Default for null/undefined or failed parsing
};

const StrykrCalendarCard = memo(({ className }: StrykrCalendarCardProps) => {
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const { session, isLoading: isSessionLoading } = useSession();
  const navigate = useNavigate();
  const today = useMemo(() => new Date(), []);
  const [shouldLoadCalendar, setShouldLoadCalendar] = useState(false);

  // Delay calendar loading to let alerts load first
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldLoadCalendar(true);
    }, 800); // Load calendar after 800ms
    return () => clearTimeout(timer);
  }, []);

  const { data: calendarData, isLoading, error } = useCalendarEvents(today, undefined, session, {
    enabled: !!session?.user?.id && !isSessionLoading && shouldLoadCalendar,
  });

  console.log('StrykrCalendarCard - Raw calendarData:', calendarData);

  // Convert calendarData.month to DayEvents[]
  const dayEvents: DayEvents[] = useMemo(() => {
    if (!calendarData?.month) return [];
    const todayStr = new Date().toISOString().split('T')[0];
    const todayData = calendarData.month[todayStr];
    if (!todayData) return [];
    return [{
      date: todayStr,
      events: [...todayData.events]
        .map((event: any) => ({
          ...event,
          title: event.title || event.event || event.name || 'Untitled Event',
        }))
        .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
        .slice(0, 5),
    }];
  }, [calendarData]);

  const handleNavigate = () => {
    navigate('/calendar');
  };

  return (
    <Card className={cn("shadow-md hover:shadow-lg transition-shadow duration-300 border border-white/5 bg-gradient-to-br from-slate-900 via-slate-950 to-black text-white", className)}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-green-400" />
          <CardTitle className="text-base font-medium text-white">Upcoming Events</CardTitle>
        </div>
        <Button variant="ghost" size="sm" className="text-xs text-green-400 hover:text-green-300 px-2 py-1 h-auto" onClick={handleNavigate}>
          View All <ArrowRight className="ml-1 h-3 w-3" />
        </Button>
      </CardHeader>
      <CardContent>
        <CardDescription className="text-xs text-white/60 mb-4">Key economic events happening soon.</CardDescription>
        <div className="space-y-4">
          {error ? (
            <div className="text-center text-red-400 text-sm p-4 bg-red-900/20 rounded-md border border-red-600/30">
              <AlertTriangle className="inline-block h-4 w-4 mr-1" /> Failed to load events.
            </div>
          ) : !isLoading && dayEvents.length === 0 ? (
            <div className="text-center text-white/60 text-sm p-4 bg-black/20 rounded-md border border-white/10">
              No events found for today.
            </div>
          ) : (
            <>
              <TodayEvents
                events={dayEvents[0]?.events || []}
                selectedEvent={selectedEvent}
                onEventClick={setSelectedEvent}
                isLoading={isLoading}
              />
              <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
                <DialogContent className="p-0 max-w-lg">
                  <EventDetails event={selectedEvent} onClose={() => setSelectedEvent(null)} />
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
});

StrykrCalendarCard.displayName = 'StrykrCalendarCard';

export default StrykrCalendarCard;
